import React from "react";
import { MultiClassFlightOffer } from "../../types/flight";
import { FaInfoCircle } from "react-icons/fa";

interface FlightOfferCardProps {
    flight: MultiClassFlightOffer;
    isOutbound: boolean;
    isSelected?: boolean;
    selectedCabinClass?: "economy" | "premiumEconomy" | "business";
    onSelect?: (
        flight: MultiClassFlightOffer,
        cabinClass: "economy" | "premiumEconomy" | "business",
        price: number,
        fullOffer: any
    ) => void;
}

const FlightOfferCard: React.FC<FlightOfferCardProps> = ({
    flight,
    isOutbound,
    isSelected,
    selectedCabinClass,
    onSelect,
}) => {
    // Get the relevant itinerary (outbound or return)
    const itinerary =
        flight.baseFlightInfo.itineraries[isOutbound ? 0 : 1] ||
        flight.baseFlightInfo.itineraries[0];
    const firstSegment = itinerary.segments[0];
    const lastSegment = itinerary.segments[itinerary.segments.length - 1];

    // Format price with space as thousand separator
    const formatPrice = (priceStr: string) => {
        const price = parseFloat(priceStr);
        const [whole, decimal] = price.toFixed(2).split(".");
        const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return `${formattedWhole},${decimal}`;
    };

    // Format time from ISO string
    const formatTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    // Format duration from PT format (e.g., "PT13H25M")
    const formatDuration = (duration: string) => {
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (!match) return duration;

        const hours = match[1] || "0";
        const minutes = match[2] || "0";

        if (minutes === "0") {
            return `${hours.padStart(2, "0")}h`;
        }
        return `${hours.padStart(2, "0")}h ${minutes.padStart(2, "0")}m`;
    };

    // Determine stop text
    const getStopText = () => {
        const stopCount = itinerary.segments.length - 1;
        if (stopCount === 0) return "Direct";
        if (stopCount === 1) return "1 stop";
        return `${stopCount} stops`;
    };

    // Get connection airports
    const getConnectionAirports = () => {
        if (itinerary.segments.length <= 1) return [];
        return itinerary.segments
            .slice(0, -1)
            .map((seg) => seg.arrival.iataCode);
    };

    // Get operator text
    const getOperatorText = () => {
        const operators = new Set();
        itinerary.segments.forEach((segment) => {
            const carrier =
                segment.operating?.carrierCode || segment.carrierCode;
            if (carrier === "SK") {
                operators.add("SAS");
            } else {
                operators.add("SAS Connect and SAS");
            }
        });
        return Array.from(operators).join(" and ");
    };

    // Calculate segment and layover durations for visual proportions
    const calculateSegmentProportions = () => {
        if (itinerary.segments.length === 1) {
            return [{ type: "flight", proportion: 100 }];
        }

        // Parse total duration to minutes
        const totalMatch = itinerary.duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        const totalMinutes =
            parseInt(totalMatch?.[1] || "0") * 60 +
            parseInt(totalMatch?.[2] || "0");

        const proportions: Array<{
            type: "flight" | "layover";
            proportion: number;
        }> = [];

        for (let i = 0; i < itinerary.segments.length; i++) {
            const segment = itinerary.segments[i];

            // Add flight segment
            const segMatch = segment.duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
            const segMinutes =
                parseInt(segMatch?.[1] || "0") * 60 +
                parseInt(segMatch?.[2] || "0");
            proportions.push({
                type: "flight",
                proportion: (segMinutes / totalMinutes) * 100,
            });

            // Add layover if not last segment
            if (i < itinerary.segments.length - 1) {
                const arrivalTime = new Date(segment.arrival.at);
                const nextDepartureTime = new Date(
                    itinerary.segments[i + 1].departure.at
                );
                const layoverMinutes =
                    (nextDepartureTime.getTime() - arrivalTime.getTime()) /
                    60000;

                proportions.push({
                    type: "layover",
                    proportion: (layoverMinutes / totalMinutes) * 100,
                });
            }
        }

        return proportions;
    };

    const connections = getConnectionAirports();
    const segmentProportions = calculateSegmentProportions();

    return (
        <div className="bg-white transition-shadow">
            <div className="flex h-full">
                {/* Left Column - Flight Info */}
                <div className="flex items-center gap-6 w-1/2 p-4">
                    {/* Times */}
                    <div className="text-left">
                        <div className="text-sm font-bold text-gray-900">
                            {formatTime(firstSegment.departure.at)} –{" "}
                            {formatTime(lastSegment.arrival.at)}
                        </div>
                        <div className="text-xs text-gray-500">
                            {getStopText()},{" "}
                            {formatDuration(itinerary.duration)}
                        </div>
                    </div>

                    {/* Info icon column */}
                    <div className="flex items-center">
                        {React.createElement(FaInfoCircle as any, {
                            className: "text-[#0103bd] w-5 h-5",
                        })}
                    </div>

                    {/* Route Visual */}
                    <div
                        className="flex flex-col -ml-2.5"
                        style={{ width: "320px" }}
                    >
                        <div className="flex items-center">
                            {/* Departure code */}
                            <span className="text-sm font-semibold text-gray-700 mr-3">
                                {firstSegment.departure.iataCode}
                            </span>

                            {/* Flight line */}
                            <div className="flex items-center flex-1 relative">
                                {/* Start circle */}
                                <div className="w-2.5 h-2.5 rounded-full bg-[#0103bd] flex-shrink-0 z-10"></div>

                                {/* Line segments */}
                                <div className="flex items-center flex-1">
                                    {segmentProportions.map(
                                        (segment, index) => (
                                            <React.Fragment key={index}>
                                                {/* Show vertical line before layover */}
                                                {segment.type === "layover" && (
                                                    <div className="w-0.5 h-3 bg-[#d5d4d1] z-10"></div>
                                                )}
                                                {/* Line segment */}
                                                <div
                                                    className={
                                                        segment.type ===
                                                        "layover"
                                                            ? "relative h-0.5"
                                                            : "h-0.5"
                                                    }
                                                    style={{
                                                        width: `${segment.proportion}%`,
                                                        backgroundColor:
                                                            segment.type ===
                                                            "flight"
                                                                ? "#0103bd"
                                                                : "#d5d4d1",
                                                    }}
                                                >
                                                    {/* Connection label centered on layover segment */}
                                                    {segment.type ===
                                                        "layover" &&
                                                        connections[
                                                            Math.floor(
                                                                index / 2
                                                            )
                                                        ] && (
                                                            <span className="text-xs text-gray-500 absolute -top-5 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                                                                {
                                                                    connections[
                                                                        Math.floor(
                                                                            index /
                                                                                2
                                                                        )
                                                                    ]
                                                                }
                                                            </span>
                                                        )}
                                                </div>
                                                {/* Show vertical line after layover */}
                                                {segment.type === "layover" && (
                                                    <div className="w-0.5 h-3 bg-[#d5d4d1] z-10"></div>
                                                )}
                                            </React.Fragment>
                                        )
                                    )}
                                </div>

                                {/* End circle */}
                                <div className="w-2.5 h-2.5 rounded-full bg-[#0103bd] flex-shrink-0 z-10"></div>
                            </div>

                            {/* Arrival code */}
                            <span className="text-sm font-semibold text-gray-700 ml-3">
                                {lastSegment.arrival.iataCode}
                            </span>
                        </div>

                        {/* Operator text */}
                        <div className="-mt-1.5">
                            <span className="text-[10px] text-gray-500">
                                Operated by {getOperatorText()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right Column - Fare Classes */}
                <div className="flex w-1/2 border-l border-gray-200">
                    {/* Go Fare (Economy) */}
                    <div
                        className={`flex-1 px-4 py-4 flex flex-col items-center justify-center cursor-pointer transition-colors ${
                            isSelected && selectedCabinClass === "economy"
                                ? "bg-gray-300"
                                : "hover:bg-gray-300"
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (flight.cabinPrices.economy && onSelect) {
                                onSelect(
                                    flight,
                                    "economy",
                                    parseFloat(
                                        flight.cabinPrices.economy.price
                                    ),
                                    flight.cabinPrices.economy.offer
                                );
                            }
                        }}
                    >
                        <div className="text-sm text-gray-600">Go</div>
                        {flight.cabinPrices.economy ? (
                            <div className="text-md font-bold text-gray-900">
                                €{formatPrice(flight.cabinPrices.economy.price)}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                Not available
                            </div>
                        )}
                    </div>

                    {/* Plus Fare (Premium Economy) */}
                    <div
                        className={`flex-1 px-4 py-4 flex flex-col items-center justify-center border-l border-gray-200 cursor-pointer transition-colors ${
                            isSelected &&
                            selectedCabinClass === "premiumEconomy"
                                ? "bg-gray-300"
                                : "hover:bg-gray-300"
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (flight.cabinPrices.premiumEconomy && onSelect) {
                                onSelect(
                                    flight,
                                    "premiumEconomy",
                                    parseFloat(
                                        flight.cabinPrices.premiumEconomy.price
                                    ),
                                    flight.cabinPrices.premiumEconomy.offer
                                );
                            }
                        }}
                    >
                        <div className="text-sm text-gray-600">Plus</div>
                        {flight.cabinPrices.premiumEconomy ? (
                            <div className="text-md font-bold text-gray-900">
                                €
                                {formatPrice(
                                    flight.cabinPrices.premiumEconomy.price
                                )}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                Not available
                            </div>
                        )}
                    </div>

                    {/* Business Fare */}
                    <div
                        className={`flex-1 px-4 py-2 flex flex-col items-center justify-center border-l border-gray-200 cursor-pointer transition-colors ${
                            isSelected && selectedCabinClass === "business"
                                ? "bg-gray-300"
                                : "hover:bg-gray-300"
                        }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (flight.cabinPrices.business && onSelect) {
                                onSelect(
                                    flight,
                                    "business",
                                    parseFloat(
                                        flight.cabinPrices.business.price
                                    ),
                                    flight.cabinPrices.business.offer
                                );
                            }
                        }}
                    >
                        <div className="text-sm text-gray-600">Business</div>
                        {flight.cabinPrices.business ? (
                            <div className="text-md font-bold text-gray-900">
                                €
                                {formatPrice(flight.cabinPrices.business.price)}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">
                                Not available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlightOfferCard;
